import { Column, SvgX } from '@grubhubprod/gh-cookbook-react';
import {
  Copy,
  Flex,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react/dist';
import { debounce } from '@grubhubprod/umami-library/dist/nodash/debounce';
import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, { useEffect, useRef, useState } from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { useParams } from 'react-router-dom';
import { SelfServiceCorp } from 'src/types/Corp';
import { AddNewPeopleCSVModal } from 'src/components/AddNewPeopleCSVModal/AddNewPeopleCSVModal';
import { AddNewPeopleModal } from 'src/components/AddNewPeopleModal/AddNewPeopleModal';
import { AddNewPeopleSuccessModal } from 'src/components/AddNewPeopleSuccessModal/AddNewPeopleSuccessModal';
import { Input } from 'src/components/Input/Input';
import { PeopleTable } from 'src/components/PeopleTable/PeopleTable';
import { Select } from 'src/components/Select/Select';
import { SortFields, SortOrder } from 'src/components/Table/SortableColumn';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { sdk } from 'src/utilities/sdk';
import {
  ISearchParams,
  SearchStatus,
  useDinerSearch,
} from 'src/utilities/useSearchHooks';
import { ROLES } from 'src/types/Employee';
import { pushError } from 'src/utilities/notifications';
import { StickyToolbarContainer } from 'src/components/People/StickyToolbarContainer';
import { AddPeopleButton } from './AddPeopleButton';

const DEBOUNCE_SEARCH_TIMING = 300;
const SORT_BY_OPTION = 'Sort by';
export const PeopleSortOptions = [
  {
    label: 'Name: A-Z',
    value: { field: SortFields.LAST_NAME, order: SortOrder.ASC },
  },
  {
    label: 'Name: Z-A',
    value: { field: SortFields.LAST_NAME, order: SortOrder.DESC },
  },
  {
    label: 'Email: A-Z',
    value: { field: SortFields.EMAIL, order: SortOrder.ASC },
  },
  {
    label: 'Email: Z-A',
    value: { field: SortFields.EMAIL, order: SortOrder.DESC },
  },
];

interface IPeopleLanding {
  showAddPeopleModalProp?: boolean;
  setShowAddPeopleModalProp?: (isAddPeopleModalShown: boolean) => void;
}

export default function PeopleLanding({
  showAddPeopleModalProp: isAddPeopleModalShown,
  setShowAddPeopleModalProp: setIsAddPeopleModalShown,
}: IPeopleLanding): JSX.Element {
  const selectSortRef = useRef(null);
  const searchInputRef = useRef(null);
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const [showToolbar, setShowToolbar] = useState(false);
  const [showAddPeopleModal, setShowAddPeopleModal] = useState(false);
  const [showAddPeopleCSVModal, setShowAddPeopleCSVModal] = useState(false);
  const [uploadedEmployeeCount, setUploadedEmployeeCount] = useState<number>(0);
  const [showAddPeopleSuccessModal, setShowAddPeopleSuccessModal] =
    useState(false);
  const [sortField, setSortField] = useState(SortFields.LAST_NAME);
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC);

  const { isPhone, isDesktop } = useScreenSizes();

  const isSearchForAdminOnly = useRef(false);

  const [corpCreatorId, setCorpCreatorId] = useState('');

  const {
    resultsList: diners,
    totalResults: totalDiners,
    searchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch: getCorpDiners,
  } = useDinerSearch(corpId as string);

  // get corp info to get the corp's creator ID
  useEffect(() => {
    async function getCorp() {
      try {
        const response = await sdk.http.get(`/corp-admin/corp/self/${corpId}`);

        const corp = get(response, 'object') as SelfServiceCorp;

        if (corp) {
          setCorpCreatorId(corp.diner_id);
        }
      } catch (error) {
        pushError(
          'Error fetching account information. Please refresh and try again.'
        );
      }
    }

    if (corpId) {
      getCorp();
    }
  }, [corpId]);

  useEffect(() => {
    if (corpId) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, corpId]);

  useEffect(() => {
    if (selectSortRef.current) {
      const option = PeopleSortOptions.find(
        (o) => o.value.order === sortOrder && o.value.field === sortField
      );

      selectSortRef.current.value = option ? option.label : '';
    }
  }, [showToolbar, selectSortRef, sortOrder, sortField]);

  useEffect(() => {
    const subscription = subscribe('navBar:hidden', (data) => {
      const value = searchInputRef.current.value;
      setShowToolbar(!data.isNavBarVisible);
      searchInputRef.current.value = value;
    });

    return () => {
      subscription();
    };
  }, []);

  useEffect(() => {
    setShowAddPeopleModal(isAddPeopleModalShown);
  }, [isAddPeopleModalShown]);

  function doSearch(searchParams: ISearchParams = {}, isLoadingMore?: boolean) {
    const query = searchInputRef.current ? searchInputRef.current.value : null;
    let params: ISearchParams = { ...searchParams };

    // checks if admin-only filter is applied
    if (isSearchForAdminOnly.current) {
      params = { ...params, diner_role: ROLES.FINANCIAL_ADMIN };
    }

    if (query) {
      params.query = query;
    }

    if (sortOrder && sortField) {
      params.sort_order = sortOrder;
      params.sort_field = sortField;
    }

    return getCorpDiners(params, isLoadingMore);
  }

  const debouncedSearch = debounce(doSearch, DEBOUNCE_SEARCH_TIMING);

  function onInput() {
    debouncedSearch();
  }

  function onSortSelect() {
    const sortLabel = selectSortRef.current.value;
    const option = PeopleSortOptions.find(
      (option) => option.label === sortLabel
    );

    setSortField(option ? option.value.field : null);
    setSortOrder(option ? option.value.order : null);
  }

  function onSortColumn(field: SortFields, order: SortOrder) {
    setSortField(field);
    setSortOrder(order);
  }

  function onAddPeopleModalSuccess() {
    searchInputRef.current.value = '';
    doSearch({ page: 1 });
  }

  function onAddPeopleSuccessfully(employeeCount = 0) {
    onAddPeopleModalSuccess();
    setShowAddPeopleSuccessModal(true);
    setUploadedEmployeeCount(employeeCount);
    setShowAddPeopleModal(false);
    setIsAddPeopleModalShown?.(false);
  }

  function onCloseAddPeopleModal() {
    setShowAddPeopleModal(false);
    setIsAddPeopleModalShown?.(false);
  }

  function onShowCSVModal() {
    onCloseAddPeopleModal();
    setShowAddPeopleCSVModal(true);
  }

  function renderSearchBar() {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="flex-end"
        flexDirection={isPhone ? 'column-reverse' : 'row'}
      >
        <Column desktop={7}>
          <Flex
            alignItems="flex-end"
            justifyContent={showToolbar ? 'space-between' : 'flex-start'}
            flexDirection={isPhone ? 'column' : 'row'}
          >
            <Column desktop={8} tablet={5}>
              <Stack
                y={isPhone ? 'xxx-sm' : 'none'}
                x={isPhone ? 'none' : 'md'}
              >
                <Input
                  key="searchTerm"
                  ariaLabel="Search by name or email"
                  placeholder="Search by name or email"
                  name="searchTerm"
                  id="searchTerm"
                  ref={searchInputRef}
                  onInput={onInput}
                  prefixIcon={
                    <Copy variant="body" color="secondaryText">
                      <SvgMagnifyingGlass size="16px" />
                    </Copy>
                  }
                />
              </Stack>
            </Column>
            {!isDesktop && (
              <Column desktop={4} tablet={3}>
                <Stack y={!showToolbar && isPhone ? 'x-sm' : 'none'}>
                  <Select
                    name="sort"
                    id="sort"
                    onChange={onSortSelect}
                    ref={selectSortRef}
                  >
                    <option value="">{SORT_BY_OPTION}</option>
                    {PeopleSortOptions.map((option) => (
                      <option key={option.label} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Stack>
              </Column>
            )}
          </Flex>
        </Column>

        {!showToolbar && (
          <Column tablet={3} phone={12}>
            <Stack y={isPhone ? 'lg' : 'none'}>
              <AddPeopleButton
                onClickAddPeopleButton={() => setShowAddPeopleModal(true)}
              />
            </Stack>
          </Column>
        )}
      </Flex>
    );
  }

  return (
    <>
      {showToolbar && (
        <StickyToolbarContainer>{renderSearchBar()}</StickyToolbarContainer>
      )}

      {!showToolbar && (
        <Stack y={isPhone ? 'xxx-sm' : 'sm'}>{renderSearchBar()}</Stack>
      )}

      {!(isPhone && showToolbar) && (
        <Stack y="x-sm">
          <Flex alignItems="flex-end" justifyContent="flex-end">
            <Copy variant="body" color="secondaryText">
              {`${diners.length} of ${totalDiners}`}
            </Copy>
          </Flex>
        </Stack>
      )}

      <PeopleTable
        isLandingTable
        corpCreatorId={corpCreatorId}
        hasMoreResults={hasMoreResults}
        loadMoreNumber={loadMoreNumber}
        corpId={corpId as string}
        getCorpDiners={doSearch}
        searchStatus={searchStatus}
        diners={diners}
        showFooter={false}
        isSortable
        bodyHeight="532px"
        previousSortField={sortField}
        previousSortOrder={sortOrder}
        onSortColumnClick={onSortColumn}
        showEmptyState={
          diners.length === 0 &&
          searchStatus !== SearchStatus.ERROR &&
          searchStatus !== SearchStatus.LOADING &&
          searchInputRef.current &&
          !searchInputRef.current.value
        }
        showNoResultsState={
          diners.length === 0 &&
          searchStatus !== SearchStatus.ERROR &&
          searchStatus !== SearchStatus.LOADING &&
          searchInputRef.current &&
          searchInputRef.current.value
        }
      />

      {showAddPeopleModal && (
        <AddNewPeopleModal
          corpId={corpId as string}
          onSuccess={onAddPeopleSuccessfully}
          onCancel={onCloseAddPeopleModal}
          onShowCSVModal={onShowCSVModal}
          closeIcon={<SvgX aria-label="close" size="24px" />}
          onClose={onCloseAddPeopleModal}
        />
      )}
      {showAddPeopleCSVModal && (
        <AddNewPeopleCSVModal
          corpId={corpId as string}
          onSuccess={onAddPeopleSuccessfully}
          onClose={() => setShowAddPeopleCSVModal(false)}
        />
      )}
      {showAddPeopleSuccessModal && (
        <AddNewPeopleSuccessModal
          employeeCount={uploadedEmployeeCount}
          onClose={() => setShowAddPeopleSuccessModal(false)}
        />
      )}
    </>
  );
}
